import { Site } from '@vromo/models';
import Button from '@vromo/ui/components/Button';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import theme from '@vromo/ui/components/theme';
import { useToasts } from '@vromo/ui/components/Toasts';
import { observer } from 'mobx-react-lite';
import { useStore } from '@vromo/mobx-react';
import React, { useState } from 'react';
import SiteTokenModal from './SiteTokenModal';
import { RootStore } from '../../stores/RootStore';
import { qaLabelTestSites } from './../../../../../common-qa-labels/sites';

export default observer(function SiteTools({ editedSite }: { editedSite: Site }) {
    const [showSiteTokenModal, setShowSiteTokenModal] = useState<boolean>(false);
    const [tokenModalType, setTokenModalType] = useState<'form' | 'map'>('form');
    const [siteToken, setSiteToken] = useState<string | null>(null);

    const showToast = useToasts();
    const {
        authStore: { graph, role }
    } = useStore<RootStore>('rootStore');

    function generateSiteToken(siteId: number): Promise<any> {
        const tokenPath = graph.root.graph().roles(role.id).sites(siteId).url().split('graph')[1];

        return graph.root
            .graph()
            .sessions()
            .post([{ type: 'site', path: tokenPath }]);
    }

    function createSiteTool(button: 'form' | 'map'): void {
        const siteId = editedSite.id;

        if (siteId) {
            generateSiteToken(siteId)
                .then(({ items }) => {
                    setSiteToken(items[0].token);
                    setShowSiteTokenModal(true);
                    setTokenModalType(button);
                })
                .catch((e) => {
                    console.error(e);
                    showToast(
                        `Something went wrong with creating this ${
                            button === 'form' ? 'job request form' : 'live view'
                        }. Try again.`,
                        'error'
                    );
                });
        }
    }

    return (
        <div style={{ marginTop: 20 }}>
            <label data-testid={qaLabelTestSites.editSiteModal.jobRequestLabel}>Job Request Form</label>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 10
                }}>
                <FontAwesome
                    icon="clipboard-list"
                    className="fa-fw"
                    style={{ color: theme.primary, fontSize: '2.5rem' }}
                    weight="regular"
                />
                <div style={{ flex: 4, padding: 10 }} data-testid={qaLabelTestSites.editSiteModal.jobRequestText}>
                    Generate a form for creating orders specifically for this site.
                </div>
                <div style={{ flex: 1 }}>
                    <Button primary onClick={() => createSiteTool('form')} data-testid={qaLabelTestSites.editSiteModal.jobRequestGenerateButton}>
                        Generate
                    </Button>
                </div>
            </div>
            <div style={{ margin: 20, borderBottom: `1px solid ${theme.lightGrey}` }}></div>
            <label data-testid={qaLabelTestSites.editSiteModal.liveSiteViewLabel}>Live Site View</label>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 10
                }}>
                <FontAwesome
                    icon="map-marked-alt"
                    className="fa-fw"
                    style={{ color: theme.primary, fontSize: '2.5rem' }}
                    weight="regular"
                />
                <div style={{ flex: 4, padding: 10 }} data-testid={qaLabelTestSites.editSiteModal.liveSiteViewText}>
                    Generate a live view of this site's jobs and drivers and track them in real
                    time.
                </div>
                <div style={{ flex: 1 }}>
                    <Button primary onClick={() => createSiteTool('map')} data-testid={qaLabelTestSites.editSiteModal.liveSiteViewGenerateButton}>
                        Generate
                    </Button>
                </div>
            </div>
            <SiteTokenModal
                isOpen={showSiteTokenModal}
                type={tokenModalType}
                onRequestClose={() => setShowSiteTokenModal(false)}
                token={siteToken}
            />
        </div>
    );
});
